import { useTranslation } from 'react-i18next'

import { AuthLayout } from '@/components/layout/auth-layout'
import { Button } from '@/components/primitives/button'
import { AuthBrandHeader } from '@/features/auth/components/brand-header'
import { LanguageSegmentedControl } from '@/features/auth/components/language-segmented-control'
import { normalizeLanguage, setAppLanguage } from '@/i18n/i18n'
import { useWecomAuthPage } from './use-wecom-auth-page'

const errorMessageKeys: Record<string, string> = {
  'auth.wecom.transaction_invalid': 'expired',
  'auth.wecom.transaction_expired': 'expired',
  'auth.wecom.oauth_unavailable': 'unavailable',
  'auth.wecom.service_unavailable': 'unavailable',
  'im.identity.account_inactive': 'accountInactive',
  'im.identity.binding_not_ready': 'bindingNotReady',
  'im.identity.conflict': 'conflict',
  'auth.wecom.authorization_incomplete': 'authorizationIncomplete',
  'auth.wecom.email_configuration_incomplete': 'configurationIncomplete',
  'auth.wecom.email_missing': 'emailMissing',
  'auth.wecom.email_not_allowed': 'emailNotAllowed',
  'auth.wecom.account_not_eligible': 'notEligible',
  'auth.wecom.account_not_found': 'accountNotFound',
  'auth.wecom.account_email_ambiguous': 'accountEmailAmbiguous',
  'auth.wecom.account_inactive': 'accountInactive',
  'auth.wecom.account_email_changed': 'emailChanged',
  'auth.wecom.account_requires_reset': 'requiresReset',
  'auth.wecom.identity_mismatch': 'identityMismatch',
  'auth.wecom.policy_changed': 'policyChanged',
  'auth.wecom.transaction_version_conflict': 'versionConflict',
}

/** 公开授权页面：服务端 context 决定流程，查询参数不能选择账号或授权动作。 */
export function WecomAuthPage() {
  const { i18n, t } = useTranslation('auth')
  const state = useWecomAuthPage()
  return (
    <AuthLayout
      actions={
        <LanguageSegmentedControl
          language={normalizeLanguage(i18n.language)}
          onLanguageChange={(language) => void setAppLanguage(language)}
          t={t}
        />
      }
      portal="user"
    >
      <div className="grid min-h-[300px] content-center gap-6">
        <AuthBrandHeader title={t('user.title')} />
        {state.loading ? (
          <p role="status">{t('wecomAuth.processing')}</p>
        ) : (
          <>
            {state.errorCode ? (
              <p role="alert" className="text-sm text-[var(--color-danger)]">
                {t(`wecomAuth.errors.${errorMessageKeys[state.errorCode] ?? 'generic'}`)}
              </p>
            ) : null}
            {state.canAuthorize ? (
              <>
                <p className="text-sm text-[var(--color-text-muted)]">
                  {t('wecomAuth.emailDescription')}
                </p>
                <Button isLoading={state.authorizing} onClick={state.authorize}>
                  {t('wecomAuth.authorizeEmail')}
                </Button>
              </>
            ) : state.canConfirm ? (
              <>
                <p className="text-sm">
                  {t('wecomAuth.currentAccount')}：{state.account.name ?? state.account.email}
                </p>
                <p className="text-sm text-[var(--color-text-muted)]">
                  {t('wecomAuth.confirmDescription')}
                </p>
                <Button isLoading={state.confirming} onClick={state.confirm}>
                  {t('wecomAuth.confirm')}
                </Button>
              </>
            ) : state.canLogin ? (
              <>
                <p className="text-sm leading-6 text-[var(--color-text-muted)]">
                  {t('wecomAuth.manualBindingHelp')}
                </p>
                <Button onClick={state.goToLogin}>{t('wecomAuth.goToLogin')}</Button>
              </>
            ) : (
              <p className="text-sm text-[var(--color-text-muted)]">{t('wecomAuth.restart')}</p>
            )}
          </>
        )}
      </div>
    </AuthLayout>
  )
}
